import { useEffect, useState } from 'react';

import type { ProviderId } from '@smartapply/shared';

import {
  SmartApplyApiError,
  fetchProviderOptions,
  validateProvider,
  type ProviderOption,
} from '../../lib/api-client.js';
import { GeminiOnboarding } from './GeminiOnboarding.js';

interface ProviderFormProps {
  onConfigured: (provider: ProviderId, modelId: string) => void;
}

export function ProviderForm({ onConfigured }: ProviderFormProps) {
  const [options, setOptions] = useState<ProviderOption[]>([]);
  // Default em Google: é o único free tier prático e suporta os 2 modos
  // (análise + Modo Tendências via Grounding).
  const [provider, setProvider] = useState<ProviderId>('google');
  const [modelId, setModelId] = useState<string>('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchProviderOptions().then(({ providers }) => {
      setOptions(providers);
      const hasGoogle = providers.some((p) => p.id === 'google');
      if (hasGoogle) {
        setProvider('google');
      } else if (providers.length > 0 && providers[0]) {
        setProvider(providers[0].id);
      }
    });
  }, []);

  // Quando muda provider, escolhe o "balanced" como default
  useEffect(() => {
    const opt = options.find((o) => o.id === provider);
    if (opt) {
      const balanced = opt.models.find((m) => m.tier === 'balanced') ?? opt.models[0];
      setModelId(balanced?.id ?? '');
    }
  }, [provider, options]);

  const currentModels = options.find((o) => o.id === provider)?.models ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setValidating(true);
    try {
      await validateProvider({ provider, modelId, apiKey });
      onConfigured(provider, modelId);
    } catch (err) {
      if (err instanceof SmartApplyApiError) {
        setError(err.message);
      } else {
        setError('Falha ao validar a chave.');
      }
    } finally {
      setValidating(false);
    }
  }

  return (
    <section>
      <div className="mb-6">
        <h2 className="font-serif text-2xl font-medium text-[var(--ink-primary)]">
          Configure a IA
        </h2>
        <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
          A análise é feita com a sua conta no provider escolhido (BYOK). A chave fica só em sessão,
          não vai pro nosso banco nem pra nossos logs.
        </p>
      </div>

      {/* Tutorial Gemini destacado — fica visível como sugestão de free tier */}
      <div className="mb-6">
        <GeminiOnboarding defaultOpen={false} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            Provider
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.map((opt) => (
              <ProviderButton
                key={opt.id}
                id={opt.id}
                active={opt.id === provider}
                onClick={() => setProvider(opt.id)}
              />
            ))}
          </div>
        </div>

        <div>
          <label
            htmlFor="modelId"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            Modelo
          </label>
          <select
            id="modelId"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          >
            {currentModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          <p className="mt-1.5 font-sans text-xs text-[var(--ink-tertiary)]">
            {modelHintFor(provider, modelId)}
          </p>
        </div>

        <div>
          <label
            htmlFor="apiKey"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            API Key
          </label>
          <div className="mt-2 flex gap-2">
            <input
              id="apiKey"
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={placeholderOfProvider(provider)}
              className="w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="rounded-sm border border-[var(--border-default)] px-3 py-2 font-sans text-xs text-[var(--ink-secondary)] hover:border-[var(--border-strong)]"
            >
              {showKey ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          <p className="mt-1.5 font-sans text-xs text-[var(--ink-tertiary)]">
            {hintOfProvider(provider)}
          </p>
        </div>

        {error && (
          <div className="rounded-sm border border-[var(--accent-crimson-border)] bg-[var(--accent-crimson-bg)] px-4 py-2.5">
            <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={validating || apiKey.length < 10}
          className="rounded-sm bg-[var(--ink-primary)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity disabled:opacity-50"
        >
          {validating ? 'Validando...' : 'Validar e continuar'}
        </button>
      </form>
    </section>
  );
}

function ProviderButton({
  id,
  active,
  onClick,
}: {
  id: ProviderId;
  active: boolean;
  onClick: () => void;
}) {
  const free = isFreeFriendly(id);
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'relative rounded-sm border px-3 py-1.5 font-sans text-sm transition-colors ' +
        (active
          ? 'border-[var(--ink-primary)] bg-[var(--ink-primary)] text-[var(--bg-paper)]'
          : 'border-[var(--border-default)] bg-[var(--bg-paper)] text-[var(--ink-secondary)] hover:border-[var(--border-strong)]')
      }
    >
      {labelOfProvider(id)}
      {free && !active && (
        <span
          className="ml-1.5 inline-block translate-y-[-1px] rounded-sm px-1 py-[1px] font-mono text-[9px] uppercase tracking-wider"
          style={{
            color: 'var(--accent-forest)',
            backgroundColor: 'var(--accent-forest-bg)',
            border: '1px solid var(--accent-forest-border)',
          }}
        >
          free tier
        </span>
      )}
    </button>
  );
}

function isFreeFriendly(id: ProviderId): boolean {
  // Em 2026: só Gemini tem free tier prático suficiente pra MVP.
  return id === 'google';
}

function labelOfProvider(id: ProviderId): string {
  return (
    (
      {
        anthropic: 'Anthropic',
        openai: 'OpenAI',
        google: 'Google',
        openrouter: 'OpenRouter',
      } as const
    )[id] ?? id
  );
}

function placeholderOfProvider(id: ProviderId): string {
  return (
    (
      {
        anthropic: 'sk-ant-api03-...',
        openai: 'sk-proj-... ou sk-...',
        google: 'AIza...',
        openrouter: 'sk-or-v1-...',
      } as const
    )[id] ?? ''
  );
}

function hintOfProvider(id: ProviderId): string {
  return (
    (
      {
        anthropic:
          'Crie em console.anthropic.com → Settings → API Keys. Free tier: ~$5 inicial, depois precisa cartão.',
        openai:
          'Crie em platform.openai.com → API keys. Atenção: free credits foram descontinuados em 2025.',
        google:
          'Crie em aistudio.google.com → Get API key. Tem free tier diário sem precisar de cartão.',
        openrouter:
          'Crie em openrouter.ai → Keys. Modelos `:free` (DeepSeek, Llama) limitados a 50 requests/dia.',
      } as const
    )[id] ?? ''
  );
}

function modelHintFor(provider: ProviderId, modelId: string): string {
  if (provider === 'google') {
    if (modelId === 'gemini-2.5-flash') {
      return 'Recomendado · free tier diário sem cartão · suporta Modo Tendências (Grounding).';
    }
    if (modelId === 'gemini-3.5-flash-lite') {
      return 'Mais rápido e com limite diário maior no free tier. Qualidade um pouco menor.';
    }
    if (modelId === 'gemini-3.6-flash') {
      return 'Geração mais recente do Gemini. Funciona no free tier.';
    }
  }
  if (provider === 'anthropic') {
    if (modelId.startsWith('claude-haiku')) {
      return 'Mais barato. Boa qualidade pra análise. ~250 análises com $5 iniciais.';
    }
    if (modelId.startsWith('claude-sonnet')) {
      return 'Recomendado pra qualidade. ~25-50 análises com $5 iniciais.';
    }
    if (modelId.startsWith('claude-opus')) {
      return 'Qualidade máxima. Caro: ~12 análises com $5 iniciais.';
    }
  }
  return '';
}
